// Receptenboek: zoeken, filteren op soort en apparaat, snel klaar, favorieten en sorteren, zonder de pagina te herladen.
// Zoeken: tijdens het typen een lijst met maximaal 5 recepten onder het veld; Enter of "Toon alle" filtert het raster en
// scrolt ernaartoe. Hoofdletters en accenten tellen niet mee ("creme" vindt "crème"), alle woorden moeten passen.
// ?q=<zoekterm> vult het zoekveld, ?device=<handle> kiest een apparaat (QR-code in de handleiding), ?category=<handle> een
// soort (link in het menu); bij device en category tellen alleen waarden die op de pagina voorkomen.

import { readFavourites, writeFavourites } from './recipe-favourites.js';

const MAX_SUGGESTIONS = 5;

function normalise(text) {
  return String(text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

class RecipeHub extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-grid]');
    if (!this.grid) return;
    this.items = [...this.grid.querySelectorAll('[data-recipe-item]')];
    this.items.forEach((item) => {
      item.haystack = normalise(`${item.dataset.search} ${item.dataset.title}`);
    });
    this.status = this.querySelector('[data-status]');
    this.empty = this.querySelector('[data-empty]');
    this.emptyText = this.querySelector('[data-empty-text]');
    this.results = this.querySelector('[data-results]');
    this.heading = this.querySelector('[data-results-heading]');
    this.count = this.querySelector('[data-count]');
    this.form = this.querySelector('[data-search-form]');
    this.search = this.querySelector('[data-search]');
    this.clear = this.querySelector('[data-search-clear]');
    this.suggest = this.querySelector('[data-suggest]');
    this.sort = this.querySelector('[data-sort]');
    this.clearDevice = this.querySelector('[data-clear-device]');
    this.texts = this.readTexts();
    this.state = { category: 'all', device: 'all', query: '', quick: false, favourites: false, sort: 'default' };
    this.active = -1;
    this.matches = [];
    try {
      this.counts = JSON.parse(this.querySelector('[data-counts]')?.textContent || '[]');
    } catch {
      this.counts = [];
    }

    this.querySelectorAll('[data-filter="category"]').forEach((button) => {
      button.addEventListener('click', () => {
        this.state.category = button.dataset.value;
        this.apply();
      });
    });
    this.querySelectorAll('[data-filter="device"]').forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        const value = link.dataset.value;
        this.state.device = this.state.device === value ? 'all' : value;
        this.apply();
        this.scrollToResults();
      });
    });
    this.querySelectorAll('[data-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        const key = button.dataset.toggle;
        this.state[key] = !this.state[key];
        this.apply();
      });
    });
    this.clearDevice?.addEventListener('click', () => {
      this.state.device = 'all';
      this.apply();
    });
    this.querySelectorAll('[data-reset]').forEach((button) => {
      button.addEventListener('click', () => this.reset());
    });
    this.querySelectorAll('[data-try-category]').forEach((button) => {
      button.addEventListener('click', () => this.tryFilter({ category: button.dataset.tryCategory }));
    });
    this.querySelectorAll('[data-try-device]').forEach((button) => {
      button.addEventListener('click', () => this.tryFilter({ device: button.dataset.tryDevice }));
    });
    this.sort?.addEventListener('change', () => {
      this.state.sort = this.sort.value;
      this.apply();
    });

    this.setupSearch();
    this.setupFavourites();
    this.readUrl();
    this.apply();
  }

  readTexts() {
    const template = this.querySelector('template[data-suggest-texts]');
    const get = (attr) => template?.content.querySelector(`[${attr}]`)?.textContent.trim() || '';
    return {
      showAllOne: get('data-show-all-one'),
      showAll: get('data-show-all'),
      none: get('data-none'),
      resultsFor: get('data-results-for'),
    };
  }

  // ---- Zoeken ----------------------------------------------------------------------------------------------------

  setupSearch() {
    if (!this.search) return;
    this.search.addEventListener('input', () => {
      this.state.query = this.search.value.trim();
      this.apply();
      this.renderSuggestions();
    });
    this.search.addEventListener('focus', () => this.renderSuggestions());
    this.search.addEventListener('keydown', (event) => this.onKey(event));
    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.commit();
    });
    this.clear?.addEventListener('click', () => {
      this.search.value = '';
      this.state.query = '';
      this.apply();
      this.closeSuggestions();
      this.writeUrl();
      this.search.focus();
    });
    document.addEventListener('click', (event) => {
      if (!this.form?.contains(event.target)) this.closeSuggestions();
    });
  }

  words() {
    return normalise(this.state.query).split(/\s+/).filter(Boolean);
  }

  onKey(event) {
    const options = this.suggest ? [...this.suggest.querySelectorAll('[role="option"]')] : [];
    if (event.key === 'ArrowDown' && options.length) {
      event.preventDefault();
      this.setActive((this.active + 1) % options.length, options);
    } else if (event.key === 'ArrowUp' && options.length) {
      event.preventDefault();
      this.setActive((this.active - 1 + options.length) % options.length, options);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const chosen = options[this.active];
      if (chosen) window.location.href = chosen.href;
      else this.commit();
    } else if (event.key === 'Escape') {
      this.closeSuggestions();
    }
  }

  setActive(index, options) {
    this.active = index;
    options.forEach((option, i) => {
      const on = i === index;
      option.classList.toggle('is-active', on);
      option.setAttribute('aria-selected', String(on));
      if (on) this.search.setAttribute('aria-activedescendant', option.id);
    });
  }

  // Zoekterm vastleggen: lijst dicht, adres bijwerken, naar het raster.
  commit() {
    this.closeSuggestions();
    this.writeUrl();
    this.search?.blur();
    this.scrollToResults();
  }

  closeSuggestions() {
    if (!this.suggest) return;
    this.suggest.hidden = true;
    this.active = -1;
    this.search?.setAttribute('aria-expanded', 'false');
    this.search?.removeAttribute('aria-activedescendant');
  }

  // Zoekterm vetgedrukt in de naam; alles via textContent, dus geen HTML uit de data.
  highlighted(title, words) {
    const fragment = document.createDocumentFragment();
    const plain = normalise(title);
    const ranges = [];
    words.forEach((word) => {
      const at = plain.indexOf(word);
      if (at > -1) ranges.push([at, at + word.length]);
    });
    ranges.sort((a, b) => a[0] - b[0]);
    let cursor = 0;
    ranges.forEach(([start, end]) => {
      if (start < cursor) return;
      fragment.append(title.slice(cursor, start));
      const mark = document.createElement('mark');
      mark.textContent = title.slice(start, end);
      fragment.append(mark);
      cursor = end;
    });
    fragment.append(title.slice(cursor));
    return fragment;
  }

  renderSuggestions() {
    if (!this.suggest || !this.search) return;
    const words = this.words();
    if (!words.length || document.activeElement !== this.search) {
      this.closeSuggestions();
      return;
    }
    this.suggest.replaceChildren();
    this.active = -1;
    const found = this.matches;
    if (!found.length) {
      const none = document.createElement('p');
      none.className = 'recipe-suggest__none';
      none.textContent = this.texts.none;
      this.suggest.append(none);
    }
    found.slice(0, MAX_SUGGESTIONS).forEach((item, index) => {
      const link = document.createElement('a');
      link.className = 'recipe-suggest__item';
      link.href = item.dataset.url;
      link.id = `${this.suggest.id}-${index}`;
      link.setAttribute('role', 'option');
      link.setAttribute('aria-selected', 'false');
      const pic = document.createElement('span');
      pic.className = 'recipe-suggest__pic';
      if (item.dataset.img) {
        const img = document.createElement('img');
        img.src = item.dataset.img;
        img.alt = '';
        img.width = 48;
        img.height = 48;
        img.loading = 'lazy';
        pic.append(img);
      }
      const text = document.createElement('span');
      text.className = 'recipe-suggest__text';
      const name = document.createElement('b');
      name.append(this.highlighted(item.querySelector('.recipe-card__title')?.textContent.trim() || '', words));
      const meta = document.createElement('small');
      meta.textContent = [item.dataset.meta, item.dataset.deviceName?.trim()].filter(Boolean).join(' · ');
      text.append(name, meta);
      link.append(pic, text);
      this.suggest.append(link);
    });
    if (found.length) {
      const all = document.createElement('button');
      all.type = 'button';
      all.className = 'recipe-suggest__all';
      all.textContent = found.length === 1 ? this.texts.showAllOne : this.texts.showAll.replace('[count]', found.length);
      all.addEventListener('click', () => this.commit());
      this.suggest.append(all);
    }
    this.suggest.hidden = false;
    this.search.setAttribute('aria-expanded', 'true');
  }

  // ---- Adres (?q=, ?device=, ?category=) ------------------------------------------------------------------------

  readUrl() {
    const params = new URLSearchParams(window.location.search);
    const query = (params.get('q') || '').slice(0, 80);
    const device = params.get('device');
    const category = params.get('category');
    let picked = false;
    if (query && this.search) {
      this.search.value = query;
      this.state.query = query;
      picked = true;
    }
    const devices = (this.dataset.deviceHandles || '').split('|').filter(Boolean);
    if (device && devices.includes(device)) {
      this.state.device = device;
      picked = true;
    }
    const categories = [...this.querySelectorAll('[data-filter="category"]')].map((button) => button.dataset.value);
    if (category && category !== 'all' && categories.includes(category)) {
      this.state.category = category;
      picked = true;
    }
    if (picked) requestAnimationFrame(() => this.scrollToResults());
  }

  writeUrl() {
    const url = new URL(window.location.href);
    if (this.state.query) url.searchParams.set('q', this.state.query);
    else url.searchParams.delete('q');
    window.history.replaceState(null, '', url);
  }

  scrollToResults() {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.results?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  }

  reset() {
    this.state = { ...this.state, category: 'all', device: 'all', query: '', quick: false, favourites: false };
    if (this.search) this.search.value = '';
    this.writeUrl();
    this.apply();
  }

  tryFilter(filter) {
    this.state = { ...this.state, category: 'all', device: 'all', query: '', quick: false, favourites: false, ...filter };
    if (this.search) this.search.value = '';
    this.writeUrl();
    this.apply();
    this.scrollToResults();
  }

  setupFavourites() {
    this.favButtons = [...this.querySelectorAll('[data-favourite]')];
    this.favToggle = this.querySelector('[data-toggle="favourites"]');
    // Zonder localStorage (privévenster, geblokkeerd) blijven de hartjes en het filter verborgen.
    if (!writeFavourites(readFavourites())) return;
    this.favButtons.forEach((button) => {
      button.hidden = false;
      button.addEventListener('click', () => {
        const handle = button.dataset.favourite;
        const list = readFavourites();
        const next = list.includes(handle) ? list.filter((item) => item !== handle) : [...list, handle];
        writeFavourites(next);
        this.apply();
      });
    });
    if (this.favToggle) this.favToggle.hidden = false;
  }

  apply() {
    const { category, device, query, quick, favourites, sort } = this.state;
    const favs = readFavourites();
    const words = this.words();

    this.querySelectorAll('[data-filter="category"]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.value === category));
    });
    this.querySelectorAll('[data-filter="device"]').forEach((link) => {
      link.setAttribute('aria-pressed', String(link.dataset.value === device));
    });
    this.querySelectorAll('[data-toggle]').forEach((button) => {
      button.setAttribute('aria-pressed', String(Boolean(this.state[button.dataset.toggle])));
    });
    this.favButtons?.forEach((button) => {
      button.setAttribute('aria-pressed', String(favs.includes(button.dataset.favourite)));
    });

    if (this.clearDevice) {
      const item = this.items.find((entry) => entry.dataset.device === device);
      this.clearDevice.hidden = device === 'all' || !item;
      const name = this.clearDevice.querySelector('[data-device-name]');
      if (name && item) name.textContent = item.dataset.deviceName.trim();
    }
    if (this.clear) this.clear.hidden = !query;

    // Lijst onder het zoekveld: alleen op de zoekterm, zodat hij overeenkomt met wat de bezoeker typt.
    // Recepten waarvan de naam past komen eerst, daarna die waarin het woord alleen in de ingrediënten of elders staat.
    this.matches = words.length ? this.items.filter((item) => words.every((word) => item.haystack.includes(word))) : [];
    const inTitle = (item) => words.every((word) => normalise(item.dataset.title).includes(word));
    this.matches.sort((a, b) => Number(inTitle(b)) - Number(inTitle(a)));

    let visible = 0;
    this.items.forEach((item) => {
      const show =
        (category === 'all' || item.dataset.category === category) &&
        (device === 'all' || item.dataset.device === device) &&
        (!quick || item.dataset.quick === 'true') &&
        (!favourites || favs.includes(item.dataset.handle)) &&
        words.every((word) => item.haystack.includes(word));
      item.hidden = !show;
      if (show) visible += 1;
    });

    const order = [...this.items];
    if (sort === 'quick') {
      order.sort((a, b) => (Number(a.dataset.minutes) || 999) - (Number(b.dataset.minutes) || 999));
    } else if (sort === 'az') {
      order.sort((a, b) => a.dataset.title.localeCompare(b.dataset.title));
    } else {
      order.sort((a, b) => Number(a.dataset.index) - Number(b.dataset.index));
    }
    order.forEach((item) => this.grid.append(item));

    const filtering = category !== 'all' || device !== 'all' || words.length > 0 || quick || favourites;
    this.classList.toggle('is-filtering', filtering);

    if (this.heading) {
      this.heading.textContent = query ? this.texts.resultsFor.replace('[terms]', query) : this.heading.dataset.default;
    }
    if (this.count) this.count.textContent = filtering ? this.counts[visible] || '' : '';
    if (this.emptyText) {
      this.emptyText.textContent = query
        ? this.emptyText.dataset.for.replace('[terms]', query)
        : this.emptyText.dataset.default;
    }
    if (this.empty) this.empty.hidden = visible > 0;
    if (this.status) this.status.textContent = this.counts[visible] || '';
  }
}

customElements.define('recipe-hub', RecipeHub);
